import type { PublicRound } from "@kalaporssi/shared";
import { species } from "@kalaporssi/shared";
import { fkg, fp, monthName, pct, sensitivity } from "../lib/format";

function Spark({ season, month }: { season: readonly number[]; month: number }) {
  const w = 72;
  const h = 18;
  const bw = w / 12;
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {season.map((v, i) => {
        const bh = Math.max(2, (v / 1.9) * h);
        return (
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed 12-month series
            key={i}
            x={i * bw + 0.5}
            y={h - bh}
            width={bw - 1.5}
            height={bh}
            rx={1}
            fill={i === month ? "var(--tag)" : "var(--line)"}
          />
        );
      })}
    </svg>
  );
}

/** Public forecast (SPEC §2.4): demand forecast, naive price, base price, uncertainty, sensitivity, season. */
export function ForecastTable({ round, collapsible = false }: { round: PublicRound; collapsible?: boolean }) {
  const title = <h3>Kysyntäennuste, {monthName(round.month).toLowerCase()}</h3>;
  const table = (
    <div className="scroll">
      <table>
        <thead>
          <tr>
            <th>Laji</th>
            <th>Kysyntä</th>
            <th>Hintaennuste €/kg</th>
            <th>Perushinta</th>
            <th>Epävarmuus</th>
            <th>Hintaherkkyys</th>
            <th>Sesonki</th>
            <th>Huomio</th>
          </tr>
        </thead>
        <tbody>
          {species.map((s) => {
            const f = round.forecast[s.id];
            const d = (f?.naivePrice ?? s.refPrice) / s.refPrice - 1;
            return (
              <tr key={s.id}>
                <td>
                  <span className="chip" style={{ background: s.color }} />
                  {s.name}
                </td>
                <td className="num">{fkg(f?.forecastDemand ?? 0)}</td>
                <td className="num">
                  <b>{fp(f?.naivePrice)}</b> <span className={d >= 0 ? "up" : "down"}>{pct(d)}</span>
                </td>
                <td className="num">{fp(s.refPrice)}</td>
                <td className="num">±{Math.round(s.volatility * 100)}&nbsp;%</td>
                <td>{sensitivity(s.elasticity)}</td>
                <td>
                  <Spark season={s.season} month={round.month} />
                </td>
                <td className="note">{s.note}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
  const hint = <span className="muted small">Hintaennuste olettaa, että valinnat jakautuvat tasaisesti.</span>;
  if (collapsible) {
    return (
      <details className="panel">
        <summary className="row spread">
          {title}
          {hint}
        </summary>
        {table}
      </details>
    );
  }
  return (
    <div className="panel">
      <div className="row spread">
        {title}
        {hint}
      </div>
      {table}
    </div>
  );
}
